// EVARA brand and content constants
export const EVARA = {
  name: "EVARA",
  fullName: "Ever Growing Excellence",
  tagline: "Empowering Healthcare Through Excellence",
  heroSubhead:
    "Healthcare transformation, analytics, quality, and capability-building for hospitals, IVF centres, fertility clinics, diagnostics, wellness organizations, and healthcare startups.",
  mission:
    "EVARA helps healthcare organisations turn data, training and quality initiatives into measurable, sustainable improvement, partnering from assessment to implementation.",
  vision:
    "To become the most trusted healthcare transformation partner across healthcare ecosystems.",
  about:
    "EVARA (Ever Growing Excellence) is a healthcare transformation studio based in India. We partner with healthcare leaders to translate insight into measurable outcomes, combining clinical depth, analytical rigor and structured capability building. We do not stop at recommendations; we walk alongside our clients from initial assessment through implementation and sustained culture change.",
  logoUrl:
    "https://customer-assets.emergentagent.com/job_eb045582-601e-4c8b-b9dc-5bb3f5ba0559/artifacts/s8tgajpa_EVARA%20LOGO%20HD.png",
  contact: {
    email: "enquiry@evara.solutions",
    phone: "+91 80882 37915",
    address: "Bengaluru, Karnataka, India",
    linkedin: "https://www.linkedin.com/company/evara-healthcare-consultancy/",
  },
};

export const VALUES = [
  { title: "Empathy", body: "Patients and people first, always." },
  { title: "Excellence", body: "Uncompromising standards in every engagement." },
  { title: "Integrity", body: "Honest counsel, transparent outcomes." },
  { title: "Innovation", body: "Evidence-led ideas, applied with rigor." },
  { title: "Accountability", body: "We own the result, not just the deck." },
  { title: "Continuous Improvement", body: "Better every quarter, every cycle." },
];

// Six-phase transformation framework
export const HETS_PHASES = [
  {
    num: "01",
    title: "Analyze",
    icon: "Search",
    summary:
      "Get a real picture of what's happening today, using data instead of assumptions.",
    bullets: [
      "Patient experience insights",
      "Operational performance baseline",
      "Statistical gap analysis",
      "Predictive trend mapping",
    ],
  },
  {
    num: "02",
    title: "Diagnose",
    icon: "Stethoscope",
    summary:
      "Figure out why performance is where it is, and where the biggest levers actually sit.",
    bullets: [
      "Root cause analysis",
      "Maturity benchmarking",
      "Significance testing",
      "Transformation roadmap",
    ],
  },
  {
    num: "03",
    title: "Design",
    icon: "PenTool",
    summary:
      "Draw up the systems, processes and service models your organisation needs next.",
    bullets: [
      "Future-state journey maps",
      "SOPs & service standards",
      "Governance framework",
      "Outcome measurement model",
    ],
  },
  {
    num: "04",
    title: "Implement",
    icon: "Rocket",
    summary:
      "Roll the change out carefully across teams, systems and patient touchpoints.",
    bullets: [
      "Pilot & scale playbooks",
      "Process change management",
      "System & workflow updates",
      "Stakeholder alignment",
    ],
  },
  {
    num: "05",
    title: "Train",
    icon: "GraduationCap",
    summary:
      "Build the clinical, behavioural and leadership capability that keeps the change alive.",
    bullets: [
      "Capability building academy",
      "Soft-skill & leadership programs",
      "Train-the-trainer certification",
      "Custom learning journeys",
    ],
  },
  {
    num: "06",
    title: "Sustain",
    icon: "Repeat",
    summary:
      "Embed the reviews, governance and culture so improvement keeps compounding.",
    bullets: [
      "Performance reviews",
      "Culture & engagement loops",
      "Quality dashboards",
      "Continuous improvement cycles",
    ],
  },
];

// Seven core services
export const SERVICES = [
  {
    key: "transformation",
    title: "Healthcare Transformation & Operational Excellence",
    icon: "ShieldCheck",
    blurb:
      "End-to-end transformation across OPD, IPD, ER and discharge, patient journey mapping, service-excellence frameworks, process redesign and operational efficiency.",
    overview:
      "Most of the time and revenue a hospital loses happens in the gaps between departments. The handoff from OPD to diagnostics. The wait between admission and bed allocation. The friction at discharge. We walk your patient journey end to end, find where value is quietly slipping out, and redesign the workflows so OPD, IPD, ER and discharge all run to the same standard. Because we do this inside your operations rather than from outside, the changes reflect how your teams actually work and stay long after we've moved on.",
    whoItsFor:
      "Hospitals, multispecialty clinics, IVF and fertility centres, diagnostic chains and healthcare startups that are scaling and starting to see symptoms like long patient wait times, bottlenecked departments, inconsistent SOP adherence or discharge delays.",
    deliverables: [
      "Current-state process maps for OPD, IPD, ER, and discharge journeys",
      "Bottleneck and root-cause analysis with quantified impact (time, cost, patient experience)",
      "Redesigned SOPs and workflow blueprints per department",
      "Service-excellence framework tailored to your care setting",
      "Change management and staff rollout plan",
      "90-day implementation roadmap with milestones",
    ],
    process: [
      { phase: "Analyze", body: "Shadow the patient journey and operational data to surface the real friction points, not assumed ones." },
      { phase: "Diagnose", body: "Root-cause the bottlenecks and quantify their impact on time, cost and patient experience." },
      { phase: "Design", body: "Redesign workflows, SOPs and service-excellence standards around the priorities identified." },
      { phase: "Implement", body: "Roll out the redesign across OPD, IPD, ER and discharge with hands-on transition support." },
      { phase: "Train", body: "Enable department heads and frontline staff so they own and can improve the new operating standard." },
      { phase: "Sustain", body: "Embed governance, cycle-time monitoring and ownership so the gains hold after EVARA exits." },
    ],
    outcomes: [
      "Reduced patient wait times and shorter length-of-stay",
      "Higher bed and resource utilization",
      "Fewer discharge delays and re-work loops",
      "20–40% reduction in patient complaints tied to operational friction",
      "A documented, repeatable operating standard your team can run independently",
    ],
    frameworkPhases: [1, 3, 4, 6],
    stats: [
      { value: 32, suffix: "%", prefix: "-", label: "OPD wait-time reduction" },
      { value: 25, suffix: "%", prefix: "+", label: "Operational efficiency gain" },
      { value: 38, suffix: "%", prefix: "-", label: "Patient complaints tied to friction" },
    ],
  },
  {
    key: "analytics",
    title: "Clinical & Business Analytics",
    icon: "BarChart3",
    blurb:
      "Clinical, IVF, business and patient experience analytics, converting healthcare data into decisions, dashboards and benchmarks.",
    overview:
      "Most healthcare organisations are sitting on years of data that has never actually changed a decision. We take what's already in your EMR, LIMS, finance and patient-feedback systems and turn it into insight you can act on. Clinical outcome trends, IVF cycle performance, occupancy, revenue cycle, patient satisfaction, cleaned up and benchmarked. Clinical judgement meets real biostatistics, so the numbers stand up to scrutiny and actually change what leadership does next.",
    whoItsFor:
      "Hospitals, IVF and fertility clinics, diagnostic labs and polyclinics that have plenty of data but not enough in-house analytics muscle to turn it into decisions. Also useful when your current reporting isn't quite standing up to clinical or regulatory scrutiny.",
    deliverables: [
      "Data audit and cleaning of existing clinical/operational datasets",
      "Clinical outcome and IVF cycle performance analysis (success rates, predictors, benchmarking)",
      "Business performance analytics (revenue cycle, occupancy, payer mix)",
      "Patient experience analytics tied to satisfaction and complaint data",
      "Statistically validated reports suitable for leadership, boards, or publication",
      "Recommendations translated into an action list, not just a report",
    ],
    process: [
      { phase: "Analyze", body: "Audit data sources, quality and gaps across clinical and business systems." },
      { phase: "Diagnose", body: "Confirm the metrics and definitions that actually drive clinical and leadership decisions." },
      { phase: "Design", body: "Build the analytical framework using appropriate statistical methods for the data type." },
      { phase: "Implement", body: "Run the analysis and stress-test findings against known clinical and business logic." },
      { phase: "Train", body: "Hand over reproducible reporting templates and equip your team to run them going forward." },
      { phase: "Sustain", body: "Establish ongoing benchmarking and refresh cadence so insight stays current, not stale." },
    ],
    outcomes: [
      "Clear, benchmarked view of clinical and business performance",
      "Statistically defensible reporting for leadership, investors, or accreditation bodies",
      "Faster, evidence-based decision-making instead of gut-feel",
      "Identification of underperforming service lines or protocols before they become losses",
      "A foundation for ongoing dashboard and KPI tracking",
    ],
    frameworkPhases: [1, 2, 3, 6],
    stats: [
      { value: 42, suffix: "%", label: "IVF success rate (age-banded)" },
      { value: 3, suffix: "×", label: "Faster leadership decisions" },
      { value: 24, suffix: "h", prefix: "<", label: "Data-to-decision latency" },
    ],
  },
  {
    key: "quality",
    title: "Quality & Accreditation Readiness",
    icon: "BadgeCheck",
    blurb:
      "NABH and JCI readiness, clinical audits, documentation reviews and quality indicator monitoring, built to pass audits and improve outcomes.",
    overview:
      "Accreditation shouldn't feel like a fire drill in the weeks before a survey. We build NABH and JCI readiness into how your teams already work every day. That means running clinical audits, reviewing documentation against the standards and setting up quality indicator monitoring that keeps you survey-ready all year, not just in the last quarter. The point isn't to pass an audit. It's to run a quality system that quietly makes patient care safer and better in the background.",
    whoItsFor:
      "Hospitals and clinics preparing for NABH or JCI accreditation (first-time or renewal), or organizations whose quality documentation and indicator tracking has fallen behind day-to-day operations.",
    deliverables: [
      "Gap analysis against NABH/JCI standards",
      "Clinical and documentation audits with corrective action plans",
      "Standardized quality indicator dashboard and monitoring cadence",
      "Mock survey and readiness assessment before the actual audit",
      "Staff training on documentation and compliance requirements",
      "Sustainable quality governance structure post-accreditation",
    ],
    process: [
      { phase: "Analyze", body: "Assess current documentation, workflows and clinical practice against the accreditation standard." },
      { phase: "Diagnose", body: "Identify gaps, non-conformities and risk areas with a corrective-action plan for each." },
      { phase: "Design", body: "Build the documentation framework, quality indicators and monitoring cadence." },
      { phase: "Implement", body: "Execute audits, close documentation gaps and stand up indicator tracking." },
      { phase: "Train", body: "Certify department leads and staff on standards, documentation and their compliance role." },
      { phase: "Sustain", body: "Establish ongoing governance and mock-audit cadence so compliance holds year-round." },
    ],
    outcomes: [
      "Audit-ready documentation and clinical processes",
      "Higher first-attempt accreditation success",
      "Continuous quality indicator visibility instead of pre-survey scrambles",
      "Reduced clinical risk and improved patient safety metrics",
      "A quality culture embedded in daily operations, not just audit season",
    ],
    frameworkPhases: [1, 2, 3, 5, 6],
    stats: [
      { value: 92, suffix: "%", label: "NABH compliance achieved" },
      { value: 88, suffix: "/100", label: "Mock-audit readiness score" },
      { value: 9, suffix: "/10", label: "Chapters audit-ready" },
    ],
  },
  {
    key: "leadership",
    title: "Leadership & Capability Development",
    icon: "GraduationCap",
    blurb:
      "Behavioural, scientific and leadership programs that turn frontline teams into a transformation engine, including train-the-trainer certification.",
    overview:
      "A new workflow or quality system only holds up if the people running it are equipped to run it. We help you build that muscle. Behavioural science, clinical leadership programmes and train-the-trainer certification, put together so your frontline and mid-management teams stop just following new processes and start owning and improving them.",
    whoItsFor:
      "Hospitals, clinics and healthcare organisations already in some form of transformation, quality or culture-change effort, and looking to build the internal capability that keeps it going once the consultants leave.",
    deliverables: [
      "Leadership and frontline capability assessment",
      "Customized behavioural and clinical leadership training curriculum",
      "Train-the-trainer certification for internal champions",
      "Coaching and mentoring support during rollout",
      "Capability scorecards to track skill development over time",
      "Sustainability plan for ongoing internal training delivery",
    ],
    process: [
      { phase: "Analyze", body: "Assess current leadership and frontline capability against the behaviours you need." },
      { phase: "Diagnose", body: "Pinpoint the capability, mindset and behaviour gaps most limiting performance today." },
      { phase: "Design", body: "Build a customised curriculum matched to your clinical and operational context." },
      { phase: "Implement", body: "Deliver training, coaching and mentoring alongside real work, not in a vacuum." },
      { phase: "Train", body: "Certify internal champions via train-the-trainer so capability lives inside your walls." },
      { phase: "Sustain", body: "Track skill development and refresh cadence so capability keeps compounding." },
    ],
    outcomes: [
      "Certified internal trainers able to sustain programs without external dependency",
      "Stronger frontline ownership of transformation and quality initiatives",
      "Improved staff engagement and retention",
      "Consistent leadership behaviour across departments",
      "A repeatable capability-building system, not a one-off workshop",
    ],
    frameworkPhases: [2, 3, 5, 6],
    stats: [
      { value: 45, suffix: "%", prefix: "+", label: "Competency uplift, pre vs post" },
      { value: 94, suffix: "%", label: "Training completion rate" },
      { value: 18, label: "Certified internal trainers" },
    ],
  },
  {
    key: "experience",
    title: "Patient Experience Improvement",
    icon: "HeartHandshake",
    blurb:
      "Diagnose, design and embed a patient-first culture, NPS lift, complaint reduction and emotionally intelligent service touchpoints.",
    overview:
      "Clinical excellence on its own doesn't win patient loyalty. How people feel at every touchpoint does. We find where the experience is quietly breaking down for your patients, redesign those touchpoints with a bit more emotional intelligence built in, and help your staff make patient-first behaviour the default rather than the exception. NPS goes up, complaints go down, and the change actually sticks.",
    whoItsFor:
      "Hospitals, IVF and fertility clinics, and diagnostic centres facing declining NPS, rising patient complaints, or looking to differentiate on experience in a competitive market.",
    deliverables: [
      "Patient journey and touchpoint diagnostic (surveys, complaint data, mystery patient audits)",
      "Root-cause analysis of experience gaps and complaint drivers",
      "Redesigned service touchpoints and communication scripts",
      "Staff training on emotionally intelligent patient interactions",
      "NPS and complaint-tracking dashboard",
      "Culture-embedding plan for sustained patient-first behaviour",
    ],
    process: [
      { phase: "Analyze", body: "Diagnose experience gaps using patient feedback, complaint data and journey audits." },
      { phase: "Diagnose", body: "Root-cause the moments and behaviours driving NPS drops and complaint spikes." },
      { phase: "Design", body: "Redesign touchpoints, scripts and service-recovery playbooks around the real pain points." },
      { phase: "Implement", body: "Roll out the new touchpoints and behaviours across departments and shifts." },
      { phase: "Train", body: "Equip staff on emotionally intelligent, patient-first interactions with practice and feedback." },
      { phase: "Sustain", body: "Embed patient-first behaviours into onboarding, reviews and NPS tracking." },
    ],
    outcomes: [
      "Measurable NPS improvement",
      "20–40% reduction in patient complaints",
      "Stronger patient loyalty and word-of-mouth referrals",
      "Staff equipped to handle sensitive patient interactions consistently",
      "A patient-first culture that persists beyond the engagement",
    ],
    frameworkPhases: [1, 2, 3, 5, 6],
    stats: [
      { value: 62, prefix: "+", label: "NPS lift after 6 months" },
      { value: 38, suffix: "%", prefix: "-", label: "Reduction in patient complaints" },
      { value: 86, suffix: "%", label: "First-call resolution rate" },
    ],
  },
  {
    key: "dashboards",
    title: "Performance Dashboards & Decision Support",
    icon: "LineChart",
    blurb:
      "Decision-grade dashboards and embedded analytics partnership, KPIs, leadership reviews and statistical rigor on tap.",
    overview:
      "Most hospital dashboards show what happened. They don't help you decide what to do next. We build the ones that do. KPIs tied to what your leadership actually reviews, refreshed on a cadence that fits your rhythm, backed by real statistical thinking rather than raw counts. And we don't hand over a dashboard and disappear. We stay on as an analytics partner, so someone is always around when the numbers need a second read.",
    whoItsFor:
      "Hospital and clinic leadership teams who need reliable, board-ready KPI reporting and want ongoing analytics support rather than a one-time report.",
    deliverables: [
      "KPI framework aligned to strategic and operational priorities",
      "Custom dashboard build (clinical, operational, and financial views)",
      "Leadership review cadence and reporting templates",
      "Statistical validation of trends and anomalies flagged in the dashboard",
      "Embedded analytics partnership with defined SLA for ongoing support",
      "Dashboard training for internal teams",
    ],
    process: [
      { phase: "Analyze", body: "Identify the KPIs that actually drive leadership decisions across clinical, ops and finance." },
      { phase: "Diagnose", body: "Confirm metric definitions, review cadence and data ownership with leadership." },
      { phase: "Design", body: "Architect the dashboard structure, data pipeline and statistical validation logic." },
      { phase: "Implement", body: "Build, deploy and connect the dashboard to source systems with a defined refresh SLA." },
      { phase: "Train", body: "Enable leaders and department heads to read, question and act on the dashboards fluently." },
      { phase: "Sustain", body: "Establish an embedded partnership for ongoing updates, deep-dive analysis and reviews." },
    ],
    outcomes: [
      "A single source of truth for leadership decision-making",
      "Faster identification of underperforming areas",
      "Statistically sound trend analysis instead of misleading raw numbers",
      "Consistent, structured leadership review cycles",
      "Ongoing analytics partnership beyond the initial build",
    ],
    frameworkPhases: [1, 3, 4, 6],
    stats: [
      { value: 128, label: "KPIs tracked across functions" },
      { value: 42, label: "Reports fully automated" },
      { value: 3, suffix: "×", label: "Faster decision latency" },
    ],
  },
  {
    key: "assessment",
    title: "Operational Assessment",
    icon: "ClipboardCheck",
    flagship: true,
    blurb:
      "EVARA's flagship diagnostic, a structured six-step review of your people, processes, technology, quality and governance that delivers a prioritized transformation roadmap.",
    overview:
      "Before any big transformation, quality or analytics engagement, there's one question every leadership team needs answered first. Where exactly are we losing value, and in what order should we fix it? Our Operational Assessment is the diagnostic that answers that. A structured, six-step review across your people, processes, technology, quality and governance, which finishes not with a generic report but with a prioritised, sequenced roadmap your team can start acting on the next Monday.",
    whoItsFor:
      "Hospitals, IVF and fertility clinics, diagnostic chains, and healthcare organizations that need an objective, comprehensive baseline before committing to a larger transformation, quality, or analytics investment.",
    deliverables: [
      "Structured assessment across five domains: people, processes, technology, quality, governance",
      "Findings report with quantified gaps and risk areas",
      "Prioritized transformation roadmap, sequenced by impact and effort",
      "Leadership presentation and readout session",
      "Recommended engagement pathway (which EVARA practices address which gaps)",
    ],
    process: [
      { phase: "Analyze", body: "Conduct structured interviews, process observation and data review across all five domains." },
      { phase: "Diagnose", body: "Synthesise findings into a quantified gap analysis with clear risk ratings." },
      { phase: "Design", body: "Build a prioritised roadmap, sequencing fixes by impact, effort and dependencies." },
      { phase: "Implement", body: "Kick off quick wins and align leadership on ownership, milestones and success metrics." },
      { phase: "Train", body: "Equip your project team to run the roadmap with rhythm and rigour." },
      { phase: "Sustain", body: "Define baseline metrics and cadence so progress is tracked, visible and defensible." },
    ],
    outcomes: [
      "A clear, objective baseline of organizational health",
      "A prioritized roadmap instead of a generic findings report",
      "Leadership alignment on what to fix first",
      "A defensible business case for follow-on transformation, quality, or analytics work",
      "The foundation for all other EVARA engagements",
    ],
    frameworkPhases: [1, 2, 3, 4, 5, 6],
    stats: [
      { value: 42, label: "Opportunities flagged (avg.)" },
      { value: 14, label: "Quick wins in first 90 days" },
      { value: 3.4, suffix: "×", label: "Projected ROI on roadmap" },
    ],
  },
];

// Six industries
export const INDUSTRIES = [
  {
    key: "hospitals",
    name: "Hospitals",
    icon: "Hospital",
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    blurb: "Multispecialty and specialty hospitals looking for one coherent partner across operations, quality and analytics.",
    overview:
      "Hospitals carry the widest operational surface area in healthcare. OPD to IPD to ER to discharge, dozens of departments, accreditation pressure, and leadership that needs board-ready numbers rather than raw data. EVARA works across this full surface, combining operational transformation, clinical analytics and quality readiness so hospital leadership gets one coherent partner instead of three disconnected vendors.",
    understand: [
      "Multi-department coordination is your biggest operational risk, not any single department's performance",
      "NABH/JCI accreditation cycles create recurring, high-stakes deadlines",
      "Leadership needs statistically defensible reporting for boards and investors, not vanity dashboards",
      "Patient experience and clinical outcomes are both under scrutiny simultaneously",
    ],
    services: ["transformation", "quality", "dashboards", "assessment"],
    outcomes: [
      "Reduced length-of-stay and improved bed utilization",
      "Higher first-attempt accreditation success",
      "Board-ready, statistically validated performance reporting",
      "20–40% reduction in patient complaints",
    ],
    stats: [
      { value: 40, prefix: "-", suffix: "%", label: "Peak reduction in patient complaints" },
      { value: 22, prefix: "-", suffix: "%", label: "Average length-of-stay reduction" },
      { value: 92, suffix: "%", label: "Accreditation first-attempt rate" },
    ],
  },
  {
    key: "ivf",
    name: "IVF & Fertility Clinics",
    icon: "HeartPulse",
    image: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    blurb: "Fertility centres balancing biostatistics-grade reporting with the emotional stakes of the patient journey.",
    overview:
      "Fertility care runs on statistics as much as medicine. Cycle success rates, predictors, benchmarking against national and international standards, all while patients navigate one of the most emotionally sensitive journeys in healthcare. EVARA's team has hands-on IVF clinical data analytics experience from inside a UAE fertility hospital, so we understand both the biostatistics and the emotional stakes without a learning curve.",
    understand: [
      "Cycle success rate reporting must hold up to clinical and regulatory scrutiny, not just look good in a deck",
      "Patient experience here is inseparable from emotional experience, and NPS and complaint drivers behave differently to general healthcare",
      "Competitive benchmarking against other fertility centres is a constant commercial pressure",
      "Clinical and business teams often work from different, disconnected data sources",
    ],
    services: ["analytics", "experience", "dashboards", "assessment"],
    outcomes: [
      "Statistically validated cycle success and predictor analysis",
      "Clear benchmarking against sector standards",
      "Improved patient experience scores through emotionally intelligent touchpoints",
      "Unified clinical and business reporting",
    ],
    stats: [
      { value: 42, suffix: "%", label: "IVF cycle success rate (age-banded)" },
      { value: 38, suffix: "%", label: "Consult-to-cycle conversion" },
      { value: 54, prefix: "+", label: "NPS lift after touchpoint redesign" },
    ],
  },
  {
    key: "diagnostic",
    name: "Diagnostic Chains",
    icon: "FlaskConical",
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    blurb: "Diagnostic networks scaling quality and throughput consistency across every location.",
    overview:
      "Diagnostic chains operate at volume and speed. Turnaround time, accuracy, and multi-location consistency are the whole business. EVARA helps diagnostic networks standardise operations across locations, build quality indicator monitoring that scales, and turn lab and patient data into decisions leadership can act on network-wide.",
    understand: [
      "Consistency across multiple locations matters more than excellence at any single site",
      "Turnaround time and accuracy are the core KPIs your reputation is built on",
      "Quality indicator monitoring needs to scale without a proportional increase in headcount",
      "Leadership needs a single view across locations, not location-by-location reports",
    ],
    services: ["quality", "dashboards", "transformation", "assessment"],
    outcomes: [
      "Standardized SOPs and quality monitoring across all locations",
      "Faster turnaround times through operational redesign",
      "Single-view network dashboards for leadership",
      "Audit-ready documentation network-wide",
    ],
    stats: [
      { value: 35, prefix: "-", suffix: "%", label: "TAT reduction across the network" },
      { value: 92, suffix: "%", label: "Quality compliance, all locations" },
      { value: 12, label: "Locations under single-view reporting" },
    ],
  },
  {
    key: "wellness",
    name: "Wellness Organizations",
    icon: "Leaf",
    image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    blurb: "Wellness brands sitting between healthcare and hospitality, where experience and outcomes both matter.",
    overview:
      "Wellness organisations sit between healthcare and hospitality. Outcomes still matter, but so does the experience, the brand and the repeat-visit economics. EVARA brings healthcare-grade operational and analytics rigour to wellness settings without imposing a clinical, sterile feel that doesn't fit the brand.",
    understand: [
      "Client retention and repeat-visit economics drive the business model as much as any single outcome metric",
      "Service touchpoints need to feel premium, not clinical",
      "Data is often underused compared to hospitals and clinics, leaving decisions to intuition",
      "Staff training needs to blend soft-skill service delivery with genuine capability",
    ],
    services: ["experience", "leadership", "analytics", "assessment"],
    outcomes: [
      "Improved client retention and NPS",
      "Data-informed decisions on services, pricing and staffing",
      "Staff equipped to deliver consistent, premium service",
      "A measurable, sustainable improvement in client experience",
    ],
    stats: [
      { value: 28, prefix: "+", suffix: "%", label: "Client retention improvement" },
      { value: 45, prefix: "+", label: "NPS lift after 6 months" },
      { value: 4.6, suffix: "/5", label: "Post-visit satisfaction score" },
    ],
  },
  {
    key: "startups",
    name: "Healthcare Startups",
    icon: "Rocket",
    image: "https://images.unsplash.com/photo-1497215842964-222b430dc094?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    blurb: "Fast-moving healthcare companies putting the right foundations in place at the right stage.",
    overview:
      "Healthcare startups move fast but often build operations and analytics as an afterthought, until an investor, regulator or scale point forces the issue. EVARA helps startups put the right operational, quality and analytics foundations in place early, sized to where the business actually is rather than an enterprise-scale build they don't need yet.",
    understand: [
      "You need investor-grade metrics and reporting well before you need enterprise infrastructure",
      "Regulatory and accreditation readiness often arrives faster than expected as you scale",
      "Operational processes built for 1 location won't survive expansion to 3",
      "Budget and speed matter as much as rigour. You need a right-sized engagement, not a six-month enterprise project",
    ],
    services: ["assessment", "analytics", "transformation", "dashboards"],
    outcomes: [
      "Investor-ready metrics and reporting",
      "Scalable operational processes built for multi-location growth",
      "Early regulatory and quality readiness",
      "A right-sized roadmap instead of enterprise over-engineering",
    ],
    stats: [
      { value: 6, suffix: " wks", label: "Time to investor-ready metrics" },
      { value: 14, label: "Quick wins delivered in first 90 days" },
      { value: 3, suffix: "×", label: "Faster than a full enterprise rebuild" },
    ],
  },
  {
    key: "pharma",
    name: "Pharmaceutical Companies",
    icon: "Pill",
    image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    blurb: "Pharma teams needing biostatistics-grade rigour applied to commercial and market-facing questions.",
    overview:
      "Pharmaceutical companies operating in clinical and commercial healthcare settings need data partners who understand both the statistical rigour required for research and regulatory contexts, and the commercial reality of the clinics and hospitals they work with. EVARA bridges that gap. Biostatistics-grade analytical rigour applied to commercial and market-facing questions.",
    understand: [
      "Statistical rigour isn't optional. Findings need to withstand scrutiny from regulatory and clinical audiences",
      "You need visibility into how your products perform inside real clinical and hospital operations",
      "Market and clinical partner relationships depend on credible, evidence-based reporting",
      "Cross-functional stakeholders (medical, commercial, regulatory) need reporting that speaks to all three",
    ],
    services: ["analytics", "dashboards", "assessment"],
    outcomes: [
      "Statistically defensible analysis suitable for regulatory and clinical scrutiny",
      "Clear visibility into real-world product performance",
      "Cross-functional reporting that serves medical, commercial and regulatory audiences alike",
    ],
    stats: [
      { value: 100, suffix: "%", label: "Reports built to withstand regulatory scrutiny" },
      { value: 24, label: "Real-world evidence datasets analysed" },
      { value: 3, label: "Stakeholder audiences unified in one view" },
    ],
  },
];

export const ENGAGEMENT_MODELS = [
  { title: "Diagnostic Assessment", body: "A focused baseline study of patient experience, operations and culture." },
  { title: "Transformation Roadmap", body: "Strategic blueprint translating insight into prioritised, measurable change." },
  { title: "Capability Building Programs", body: "Structured learning journeys for leaders, managers and frontline staff." },
  { title: "Retainer Consulting", body: "Ongoing advisory for quality, operations and patient experience leadership." },
  { title: "Analytics Support", body: "Embedded analytics partnership for KPIs, dashboards and statistical rigor." },
  { title: "Long-Term Partnership", body: "End-to-end transformation across multiple cycles and outcomes." },
];

// Business impact
export const IMPACT = [
  { stat: "+38%", label: "Improvement in patient satisfaction" },
  { stat: "+25%", label: "Operational efficiency gains" },
  { stat: "20–40%", label: "Reduction in patient complaints" },
  { stat: "↑ NABH", label: "Stronger quality performance" },
  { stat: "+45%", label: "Stronger leadership capability" },
  { stat: "3×", label: "Faster data-driven decisions" },
];

// Why EVARA, 6 differentiators
export const WHY_EVARA = [
  {
    title: "Healthcare-focused expertise",
    body: "Built by clinicians, analysts and operators with deep healthcare DNA, not generalist consultants.",
    icon: "Stethoscope",
  },
  {
    title: "Data-driven approach",
    body: "Every recommendation is grounded in statistically validated baselines and outcome metrics.",
    icon: "BarChart3",
  },
  {
    title: "Customized solutions",
    body: "Frameworks are tailored to your organisation, specialty mix and stage of maturity.",
    icon: "Settings2",
  },
  {
    title: "Measurable outcomes",
    body: "We agree the KPIs before we begin, and we track them through to sustained change.",
    icon: "Target",
  },
  {
    title: "End-to-end implementation",
    body: "We do not stop at recommendations. We walk alongside through pilot, scale and sustain.",
    icon: "Workflow",
  },
  {
    title: "Sustainable capability",
    body: "Engagements are designed to leave behind capability, not dependency, your team owns it next time.",
    icon: "Sprout",
  },
];

export const TEAM = [
  {
    name: "Dr. Monu Jain, PhD",
    role: "Healthcare Operations & Customer Experience Leader",
    bio: "PhD with 17+ years of experience in healthcare quality, accreditation, digital transformation and patient engagement. Specialises in NABH/JCI standards, clinical workflows, team leadership, scientific training and customer experience, helping healthcare organisations deliver exceptional patient experiences alongside operational excellence.",
    image:
      "https://customer-assets.emergentagent.com/job_wellness-hub-1604/artifacts/o8h2vu7j_WhatsApp%20Image%202026-06-15%20at%204.49.27%20PM.jpeg",
  },
  {
    name: "Manroop Singh",
    role: "Head of Strategic Partnerships",
    bio: "17+ years building strategic relationships and driving transformational growth across industries. At EVARA, leads collaborations with healthcare organisations and ecosystem partners, translating vision into action through a relationship-first approach that advances patient experience, quality and workforce capability.",
    image:
      "https://customer-assets.emergentagent.com/job_wellness-hub-1604/artifacts/3p5dbfoi_WhatsApp%20Image%202026-06-14%20at%2012.00.59%20PM.jpeg",
  },
  {
    name: "Ms. Shalini Sachdeva",
    role: "Leadership & Communication Excellence Expert",
    bio: "Two decades of professional experience in leadership, behavioural sciences and communication excellence. Drives capability building and culture transformation.",
    image: null,
    initials: "SS",
  },
  {
    name: "Ms. Sahana Chandrappa",
    role: "Biostatistician & Healthcare Analytics Professional",
    bio: "3+ years of experience in clinical research, IVF analytics and healthcare reporting. Brings data intelligence and analytical rigor to every engagement.",
    image: "/team/sahana_blurred.jpg",
  },
];

export const TIME_SLOTS = [
  "09:30 AM",
  "10:30 AM",
  "11:30 AM",
  "12:30 PM",
  "02:30 PM",
  "03:30 PM",
  "04:30 PM",
  "05:30 PM",
];

export const SERVICE_OPTIONS = [
  "Healthcare Transformation & Operational Excellence",
  "Clinical & Business Analytics",
  "Quality & Accreditation Readiness",
  "Leadership & Capability Development",
  "Patient Experience Improvement",
  "Performance Dashboards & Decision Support",
  "Operational Assessment",
  "Other / Not Sure",
];

export const OPERATIONAL_ASSESSMENT = {
  tagline: "Optimize. Transform. Excel.",
  intro:
    "Every organization has opportunities to improve efficiency, quality, compliance, and customer experience. EVARA's Operational Assessment provides a structured evaluation of your current operations, helping identify strengths, bottlenecks, process inefficiencies, compliance gaps, and opportunities for sustainable improvement.",
  promise:
    "From understanding your current state to implementing future-ready solutions, EVARA helps organizations build smarter, safer, and more efficient operations.",
  challenges: [
    "Increasing operational costs",
    "Process inefficiencies",
    "Inconsistent service delivery",
    "Customer complaints",
    "Compliance risks",
    "Low productivity",
    "Resource underutilization",
    "Lack of standardized processes",
    "Limited visibility into performance",
    "Challenges in scaling operations",
  ],
  flow: [
    "Understand",
    "Assess",
    "Analyze",
    "Identify Opportunities",
    "Recommend Solutions",
    "Implementation Roadmap",
    "Continuous Improvement",
  ],
  methodology: [
    {
      key: "discovery",
      title: "Discovery",
      icon: "Search",
      blurb: "We begin by understanding your organization.",
      items: [
        "Leadership discussions",
        "Business objective review",
        "Stakeholder interviews",
        "Existing process review",
        "Operational data collection",
        "Documentation review",
      ],
      deliverable: "Current State Understanding",
    },
    {
      key: "assessment",
      title: "Operational Assessment",
      icon: "ClipboardCheck",
      blurb: "Our consultants evaluate every operational function.",
      items: [
        "Organizational structure",
        "Operational workflow",
        "Process efficiency",
        "Service delivery",
        "Workforce utilization",
        "Resource management",
        "Customer experience",
        "Digital adoption",
        "Quality systems",
        "Governance & risk",
      ],
      deliverable: "Operational Assessment Report",
    },
    {
      key: "mapping",
      title: "Process Mapping",
      icon: "Workflow",
      blurb: "We document and evaluate existing workflows.",
      items: [
        "End-to-end workflows",
        "Approval processes",
        "Turnaround times",
        "Hand-offs",
        "Process bottlenecks",
        "Redundancies",
        "Automation opportunities",
      ],
      deliverable: "Process Maps & Workflow Analysis",
    },
    {
      key: "performance",
      title: "Performance Analysis",
      icon: "BarChart3",
      blurb: "We evaluate operational performance using measurable indicators.",
      items: [
        "Productivity",
        "Resource utilization",
        "Service quality",
        "Customer satisfaction",
        "Operational efficiency",
        "Compliance performance",
        "Cost optimization",
        "Turnaround time",
        "Capacity utilization",
      ],
      deliverable: "Performance Dashboard",
    },
    {
      key: "opportunities",
      title: "Improvement Opportunities",
      icon: "Lightbulb",
      blurb: "Based on the assessment, we identify opportunities for improvement.",
      items: [
        "Workflow redesign",
        "Process standardization",
        "Technology integration",
        "Workforce optimization",
        "Automation opportunities",
        "Cost reduction",
        "Service enhancement",
        "Governance improvements",
        "Risk mitigation",
      ],
      deliverable: "Opportunity Assessment Report",
    },
    {
      key: "roadmap",
      title: "Transformation Roadmap",
      icon: "Route",
      blurb: "We provide a practical implementation plan.",
      items: [
        "Priority initiatives",
        "Quick wins",
        "Medium-term improvements",
        "Long-term transformation",
        "Resource requirements",
        "Timeline & ownership matrix",
        "Success metrics",
      ],
      deliverable: "Operational Transformation Roadmap",
    },
  ],
  assessAreas: [
    {
      name: "People",
      icon: "Users",
      items: [
        "Organization structure",
        "Roles & responsibilities",
        "Workforce capability",
        "Training needs",
        "Productivity",
        "Skill assessment",
      ],
    },
    {
      name: "Processes",
      icon: "Workflow",
      items: [
        "Standard operating procedures",
        "Workflow efficiency",
        "Documentation",
        "Process consistency",
        "Escalation mechanisms",
        "Decision-making",
      ],
    },
    {
      name: "Technology",
      icon: "Cpu",
      items: [
        "Digital maturity",
        "Software utilization",
        "System integration",
        "Reporting capabilities",
        "Automation opportunities",
        "Data management",
      ],
    },
    {
      name: "Quality",
      icon: "BadgeCheck",
      items: [
        "Quality assurance",
        "Service standards",
        "Audit readiness",
        "Continuous improvement",
        "Performance monitoring",
        "Customer feedback management",
      ],
    },
    {
      name: "Governance",
      icon: "ShieldCheck",
      items: [
        "Policies",
        "Compliance",
        "Risk management",
        "Accountability",
        "Reporting structure",
        "Performance reviews",
      ],
    },
  ],
  deliverables: [
    "Executive Summary",
    "Current State Assessment",
    "Process Maps",
    "Operational Scorecard",
    "Risk Register",
    "Improvement Opportunity Matrix",
    "Priority Action Plan",
    "Transformation Roadmap",
    "KPI Dashboard",
    "Implementation Recommendations",
  ],
  benefits: [
    "Enhanced operational efficiency",
    "Standardized workflows",
    "Improved customer experience",
    "Better resource utilization",
    "Stronger governance",
    "Reduced operational risks",
    "Increased productivity",
    "Better decision-making through data",
    "Scalable operational processes",
    "Sustainable performance improvements",
  ],
  glance: [
    { phase: "Discovery", activities: "Stakeholder interviews, document review", deliverable: "Current State Summary" },
    { phase: "Assessment", activities: "Operational evaluation", deliverable: "Assessment Report" },
    { phase: "Process Mapping", activities: "Workflow analysis", deliverable: "Process Maps" },
    { phase: "Analysis", activities: "Performance review", deliverable: "Operational Scorecard" },
    { phase: "Recommendations", activities: "Improvement planning", deliverable: "Opportunity Matrix" },
    { phase: "Roadmap", activities: "Implementation planning", deliverable: "Transformation Roadmap" },
  ],
  whyEvara: [
    "Experienced multidisciplinary consultants",
    "Practical, evidence-based recommendations",
    "Industry-specific expertise",
    "Data-driven operational insights",
    "Customized improvement strategies",
    "End-to-end implementation support",
    "Focus on measurable business outcomes",
    "Commitment to continuous improvement",
  ],
};

export const IMAGES = {
  hero:
    "https://images.pexels.com/photos/33812025/pexels-photo-33812025.jpeg?auto=compress&cs=tinysrgb&w=1600",
  about:
    "https://images.pexels.com/photos/7108344/pexels-photo-7108344.jpeg?auto=compress&cs=tinysrgb&w=1400",
  framework:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
  services:
    "https://images.unsplash.com/photo-1752835262883-975a5f0a2950?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  consultation:
    "https://images.pexels.com/photos/5452228/pexels-photo-5452228.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

// Value pillars — compact horizontal strip (Home)
export const PILLARS = [
  { title: "Rigor", body: "Clinical depth and biostatistics-grade analysis.", icon: "Microscope" },
  { title: "Depth", body: "Healthcare-specific expertise, not generic consulting.", icon: "Stethoscope" },
  { title: "Speed", body: "Right-sized engagements. No six-month lag.", icon: "Rocket" },
  { title: "Ownership", body: "We work inside operations, not around them.", icon: "HeartHandshake" },
  { title: "Impact", body: "Outcomes agreed before scope. Measured after.", icon: "Target" },
];

// EVARA experts — role-based, no invented names
export const EXPERTS = [
  {
    role: "Healthcare Consultant",
    icon: "Stethoscope",
    body: "A senior partner for hospital and clinic leadership across operations, quality and strategy, with a decade of engagements behind them.",
  },
  {
    role: "Clinical Analytics Lead",
    icon: "BarChart3",
    body: "Biostatistics-grade analysis rooted in real clinical experience, including hands-on IVF outcome analytics from inside a UAE fertility hospital.",
  },
  {
    role: "Quality & Accreditation Director",
    icon: "BadgeCheck",
    body: "NABH and JCI readiness expertise built through multiple first-attempt accreditation wins across multispecialty hospitals.",
  },
  {
    role: "Capability & Culture Lead",
    icon: "GraduationCap",
    body: "Behavioural science and clinical leadership programmes that turn frontline teams into a transformation engine, not passengers.",
  },
];
